import type { SecretId } from "@repo/app-types/entities/secret";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { DialogTrigger } from "react-aria-components";
import { useListSecretsQuery } from "../../../api/entities/secret.js";
import { ManageSecretDialog } from "../../secrets/components/manageSecretDialog.js";
import { SecretTable } from "../../secrets/components/secretTable.js";
import { DeleteSecretModal } from "./deleteSecretModal.js";

/** All of the company's secrets, to create, edit and delete. */
export const SecretsSection: FC = () => {
  const { data: secrets, isLoading, isError } = useListSecretsQuery();

  const [editingId, setEditingId] = useState<SecretId>();
  const [deletingId, setDeletingId] = useState<SecretId>();
  const editingSecret = secrets?.find(({ id }) => id === editingId);
  const deletingSecret = secrets?.find(({ id }) => id === deletingId);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-foreground">Secrets</h2>
        <DialogTrigger>
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.SMALL}
            icon={<IconPlus className="h-4 w-4" />}
          >
            Add secret
          </Button>
          <ManageSecretDialog
            secret={undefined}
            isCreateOnly
            // The list refetches on its own once the secret is saved.
            onSubmit={() => {}}
          />
        </DialogTrigger>
      </div>

      <SecretTable
        aria-label="Secrets"
        secrets={secrets}
        isLoading={isLoading}
        isError={isError}
        emptyMessage="No secrets yet."
        renderActions={(secret) => (
          <>
            <IconButton
              icon={<IconPencil className="h-4 w-4" />}
              variant={ButtonVariant.Secondary}
              accessibleLabel={`Edit ${secret.name}`}
              onClick={() => setEditingId(secret.id)}
            />
            <IconButton
              icon={<IconTrash className="h-4 w-4" />}
              variant={ButtonVariant.Secondary}
              accessibleLabel={`Delete ${secret.name}`}
              onClick={() => setDeletingId(secret.id)}
            />
          </>
        )}
      />

      <ManageSecretDialog
        isOpen={editingSecret !== undefined}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setEditingId(undefined);
          }
        }}
        secret={editingSecret}
        onSubmit={() => {}}
      />

      <DeleteSecretModal
        secret={deletingSecret}
        onClose={() => setDeletingId(undefined)}
      />
    </section>
  );
};
